import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {WGGameConfig} from "../WGGameConfig";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {WGParsedInitData} from "../server/data/WGParsedInitData";
import {GameMode} from "@nolimitcity/slot-game/bin/core/gamemode/GameMode";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {SpineTween} from "@nolimitcity/slot-game/bin/core/animation/SpineTween";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {Linear, TimelineMax, TweenLite} from "gsap";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {SoundManager} from "../resource/sound/SoundManager";
import {WGSoundConfig} from "../WGSoundConfig";

export class WGStickyMultiplier extends BaseView {
    private _reelAreaMulitplier: PIXI.BitmapText;
    public _wildReelsiD: number [] = [-1, 1, 1, 1, 1, -1];
    private _stickyMulitplierSpine: PIXI.spine.Spine;
    private _initDataParsed: boolean = false;
    private _resourceLoaded: boolean = false;
    private _initData: WGParsedInitData;
    private tl: TimelineMax;
    private _layer: ScalableLayer;
    private _orientation: Orientation;
    private _energy: PIXI.Sprite;

    protected initAnimations(): void {
        this._layer = StageManager.getLayer(WGGameConfig.instance.LAYERS.REEL_OVERLAY.name);
        this._stickyMulitplierSpine = new PIXI.spine.Spine(GameResources.getSpineAsset("fs_mult_meter"));
        this._stickyMulitplierSpine.visible = false;
        this._layer.addChild(this._stickyMulitplierSpine);
        this._resourceLoaded = true;
        this.createMultiplier();
        if (this._initData.mode === GameMode.FREESPIN) {
            this.showReelAreaMulitplier(this._initData.freespinMultiplier.toString());
        }
        this._reelAreaMulitplier.pivot.set(this._reelAreaMulitplier.width / 2, this._reelAreaMulitplier.height / 2);
        new SpineTween(this._stickyMulitplierSpine, 0, "multi_upgrade");

        this.createMultiplierTrail();
    }

    protected addEventListeners(): void {
        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event: GameEvent) => {
            this._initDataParsed = true;
            this._initData = event.params[0];
            if (this._initData.mode !== GameMode.NORMAL) {
                this.showReelAreaMulitplier(this._initData.freespinMultiplier.toString());
            }
        });
    }

    /**
     * function to create multiplier
     * with 00 -> starting countdown from 0
     * 01 to dislay untill multiplier increases in value
     */
    private createMultiplier(): void {
        this._reelAreaMulitplier = new PIXI.BitmapText("x01", {
            fontName: "WGNumbersWinCountUp",
            fontSize: 13
        });
        this._reelAreaMulitplier.visible = false;
        this._layer.addChild(this._reelAreaMulitplier);
        this.tl = new TimelineMax();
        this.hideReelAreaMulitplier();
    }

    // #233
    private createMultiplierTrail(): void {
        this._energy = new PIXI.Sprite(GameResources.getTextures("energyBall")[0]);
        this._energy.visible = false;
        this._energy.anchor.set(0.5);
        this._energy.blendMode = PIXI.BLEND_MODES.ADD;
        this._energy.scale.set(0.2);

        StageManager.getLayer(WGGameConfig.instance.LAYERS.STICKY.name).addChild(this._energy);
    }

    /**
     * function to update mulitplier for wild nudge
     * @param reelId
     * @param noNudge
     * @param symId
     */
    public updateMulitplier(reelId: number, noNudge: boolean = false, symId: number = 1): void {
        this.tl.totalProgress(1);
        // #233
        this.playEnergy(reelId, symId);

        new SpineTween(this._stickyMulitplierSpine, 0, "multi_upgrade");
        let initial = this.getCurrentMuliplierNumber();
        let value: string = (initial + (noNudge ? 1 : (++this._wildReelsiD[reelId]))).toString();
        this._wildReelsiD[reelId] = 0;

        this.tl.add(TweenLite.fromTo(this._reelAreaMulitplier.scale, 0.1, {x: 1, y: 1}, {
            x: 1.2, y: 1.2
        }));
        this.tl.add(() => {
            this._reelAreaMulitplier.text = "x" + ((value.length >= 2) ? value : "0" + value);
        });
        this.tl.add(TweenLite.fromTo(this._reelAreaMulitplier.scale, 0.1, {x: 1.2, y: 1.2}, {x: 1, y: 1}));
    }

    private playEnergy(reelId: number, symId: number): void {
        const symWidth: number = 120,
            symHeight: number = 199;

        if (symId === 3) {
            symId -= 1;
        }

        this._energy.x = ((reelId * symWidth) + (symWidth / 2));
        this._energy.y = ((symId + 1) * symHeight);

        this._energy.scale.set(0.2);
        this._energy.visible = true;
        const x1: number = (this._energy.x + this._reelAreaMulitplier.x) * 0.5,
            y1: number = (this._energy.y + this._reelAreaMulitplier.y) * 0.5 - (25 * (symId + 1));
        let time: number = 0.1;
        if (reelId === 1 || reelId === 2) {
            time = 0.15;
        }

        SoundManager.playSoundEffect(WGSoundConfig.instance.FS_FIRE_BALL);

        TweenLite.to(this._energy.scale, 0.05, {x: 0.7, y: 0.7});
        TweenLite.to(this._energy.scale, 0.05, {x: 0.2, y: 0.2, delay: (2 * time) - 0.05});

        TweenLite.to(this._energy, time, {x: x1, y: y1, ease: Linear.easeNone});
        TweenLite.to(this._energy, time, {x: this._reelAreaMulitplier.x, y: this._reelAreaMulitplier.y, delay: time});

        TweenLite.delayedCall((2 * time), () => this._energy.visible = false);
    }

    /**
     * function to get current multiplier amount
     * so that add number on it accordingly
     */
    private getCurrentMuliplierNumber(): number {
        let value: number = 0;
        const len = this._reelAreaMulitplier.text.length;
        for (let i: number = 1; i < len; i++) {
            if (len > 1) {
                value = (value * 10) + Number(this._reelAreaMulitplier.text[i]);
            } else {
                value += Number(this._reelAreaMulitplier.text[i]);
            }
        }
        return value;
    }

    public hideReelAreaMulitplier(): void {
        this._reelAreaMulitplier.visible = false;
        this._stickyMulitplierSpine.visible = false;
        this._reelAreaMulitplier.text = "x01";
    }

    public showReelAreaMulitplier(mulutplier: string = "1",): void {
        if (this._resourceLoaded && this._initDataParsed) {
            this._reelAreaMulitplier.visible = true;
            this._stickyMulitplierSpine.visible = true;
            let multi = mulutplier.length < 2 ? ("0" + mulutplier) : mulutplier;
            this._reelAreaMulitplier.text = mulutplier.length ? "x" + multi : "x01";
        }
    }

    /**
     * Override this to change position of multiplier
     * on landscape and portrait
     * @param resizeData
     */
    protected onResize(resizeData: IResizeData): void {
        if (this._orientation !== resizeData.orientation) {
            this._energy.visible = false;
        }
        this._orientation = resizeData.orientation;
        let posX = WGGameConfig.instance.REEL_ENGINE_CONFIG.DIM.WIDTH * 6;
        if (resizeData.orientation === Orientation.LANDSCAPE) {
            this._reelAreaMulitplier.position.set(posX + 70, 146);
            this._stickyMulitplierSpine.position.set(posX + 68, 142);
        } else {
            this._reelAreaMulitplier.position.set(posX * 0.84, 24);
            this._stickyMulitplierSpine.position.set((posX * 0.84), 20);
        }
    }
}
